/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";
import { Pencil, Power, Trash2, MessageSquare } from "lucide-react";
import { useEffect, useState } from "react";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import {
    userConfirmAction,
    userError,
    userSuccess,
} from "../../../utils/toastUtils";
import TestimonialModal from "../../../components/AdminSideComponents/testimonials/TestimonialModal";

const AdminTestimonials = () => {
    const API = process.env.REACT_APP_API_URL;

    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);

    const [modalOpen, setModalOpen] = useState(false);
    const [editingTestimonial, setEditingTestimonial] = useState(null);

    const fetchTestimonials = async () => {
        try {
            const res = await axios.get(`${API}/testimonials/admin`);
            setTestimonials(res.data.data || []);
        } catch (err) {
            console.error("Failed to load testimonials", err);
            setTestimonials([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTestimonials();
    }, []);

    const handleDelete = async (id) => {
        const confirmed = await userConfirmAction({
            title: "Delete Testimonial?",
            text: "This action cannot be undone.",
            confirmText: "Yes, Delete",
        });

        if (!confirmed) return;

        try {
            await axios.delete(`${API}/testimonials/${id}`);
            userSuccess("Testimonial deleted successfully");
            fetchTestimonials();
        } catch (err) {
            userError("Failed to delete testimonial");
        }
    };

    const handleToggle = async (id, isActive) => {
        const confirmed = await userConfirmAction({
            title: isActive ? "Deactivate Testimonial?" : "Activate Testimonial?",
            text: isActive
                ? "This testimonial will be hidden from users."
                : "This testimonial will be visible to users.",
            confirmText: isActive ? "Deactivate" : "Activate",
        });

        if (!confirmed) return;

        try {
            await axios.patch(`${API}/testimonials/toggle/${id}`);
            userSuccess(
                isActive ? "Testimonial deactivated" : "Testimonial activated"
            );
            fetchTestimonials();
        } catch (err) {
            userError("Failed to update status");
        }
    };

    return (
        <div className="bg-gradient-to-br from-[#FFF7F3] to-gray-50 min-h-screen">
            <div className="px-6 py-2">
                <Breadcrumb
                    title1="Testimonials"
                    title2="Manage client feedback"
                    showBtn={true}
                    buttonName="Add Testimonial"
                    onBtnClick={() => {
                        setEditingTestimonial(null);
                        setModalOpen(true);
                    }}
                />
            </div>

            <div className="px-6 py-6">
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div
                                key={i}
                                className="h-64 rounded-xl bg-gray-200 animate-pulse"
                            />
                        ))}
                    </div>
                ) : testimonials.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-5">
                            <MessageSquare className="w-10 h-10 text-gray-500" />
                        </div>

                        <h3 className="text-xl font-semibold text-gray-800">
                            No testimonials yet
                        </h3>

                        <p className="text-gray-500 mt-2 max-w-md">
                            Add your first testimonial and it will appear here.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {testimonials.map((item) => (
                            <div
                                key={item._id}
                                className="group relative bg-white rounded-2xl p-6 border border-[#F7BFAD]/40 shadow-md hover:shadow-xl transition-all duration-300"
                            >
                                <div className="flex flex-col h-full">
                                    {/* Text */}
                                    <p className="text-gray-600 text-sm leading-relaxed italic mb-6 line-clamp-4">
                                        "{item.text}"
                                    </p>

                                    {/* Rating */}
                                    <div className="flex justify-center mb-6">
                                        {[...Array(5)].map((_, i) => (
                                            <svg
                                                key={i}
                                                className={`w-4 h-4 ${i < item.rating
                                                    ? "fill-amber-400 text-amber-400"
                                                    : "fill-gray-200 text-gray-200"
                                                    }`}
                                                viewBox="0 0 24 24"
                                            >
                                                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                                            </svg>
                                        ))}
                                    </div>

                                    {/* User */}
                                    <div className="mt-auto text-center">
                                        <p className="font-semibold text-gray-900 text-sm">
                                            {item.name} 
                                        </p>

                                        <p className="text-xs text-gray-400 mt-1">
                                            {new Date(item.createdAt).toLocaleDateString()}
                                        </p>

                                        <span
                                            className={`inline-block mt-3 px-3 py-1 text-xs rounded-full ${item.isActive
                                                ? "bg-green-100 text-green-700"
                                                : "bg-gray-200 text-gray-500"
                                                }`}
                                        >
                                            {item.isActive ? "Active" : "Inactive"}
                                        </span>
                                    </div>
                                </div>

                                {/* Hover Actions */}
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-4">
                                    <button
                                        onClick={() => {
                                            setEditingTestimonial(item);
                                            setModalOpen(true);
                                        }}
                                        className="bg-white p-3 rounded-full hover:scale-110 transition"
                                        title="Edit"
                                    >
                                        <Pencil className="w-4 h-4 text-gray-700" />
                                    </button>

                                    <button
                                        onClick={() =>
                                            handleToggle(item._id, item.isActive)
                                        }
                                        className={`p-3 rounded-full transition ${item.isActive
                                            ? "bg-blue-500 hover:bg-blue-600"
                                            : "bg-gray-500 hover:bg-gray-600"
                                            }`}
                                        title="Toggle Status"
                                    >
                                        <Power className="w-4 h-4 text-white" />
                                    </button>

                                    <button
                                        onClick={() => handleDelete(item._id)}
                                        className="bg-red-500 p-3 rounded-full hover:bg-red-600 transition"
                                        title="Delete"
                                    >
                                        <Trash2 className="w-4 h-4 text-white" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Placeholder Modal */}
            {modalOpen && (
                <TestimonialModal
                    onClose={() => {
                        setModalOpen(false);
                        setEditingTestimonial(null);
                    }}
                    editingTestimonial={editingTestimonial}
                    fetchTestimonials={fetchTestimonials}
                />
            )}
        </div>
    );
};

export default AdminTestimonials;